import Icon from 'icons/Icon'
import React, { useState, useMemo } from 'react'
import uniqBy from 'lodash/uniqBy'
import GameProviderFilters from './GameProviderFilters'
import { useSelector } from 'react-redux'
import { capitalizeMyText } from 'utils/helpers'
import { matchesProviderSearch } from 'utils/provider'
import { matchesStudioSearch } from 'utils/studio'
import { GAME_FILTER_SUPPORT_TYPE } from '../../constants/uiConfig'

/**
 * Game filters checkbox component for filtering game listings
 *
 * @param {Object} props - Component props
 * @param {Array} props.dataSet - Array of filter data items
 * @param {Object} props.selectedFilter - Object containing currently selected filter values for each filter type
 * @param {Function} props.setSelectedFilter - Function to update selected filters
 * @param {string} props.dataType - Type of filter data ('providers'|'studios'|'rating')
 * @param {string} props.title - Title text for the filter section
 * @param {string} props.icon - Icon name for the filter section
 * @param {'default' | 'provider-accordion'} [props.type='default'] - Visual style type for the component
 */
const GameFiltersCheckbox = ({
  dataSet,
  selectedFilter,
  setSelectedFilter,
  dataType,
  title,
  icon,
  type = 'default'
}) => {
  const selectedFilterData = selectedFilter?.[dataType] || []

  const uiConfig = useSelector((state) => state?.uiConfig?.general)
  const { gameFilterSupportType = GAME_FILTER_SUPPORT_TYPE.PROVIDER_AND_STUDIO } = uiConfig || {}
  const { languageData } = useSelector((state) => state.language)

  // Search state
  const [query, setQuery] = useState('')
  const searchQuery = (query || '').trim().toLowerCase()

  // Filter data based on search query and sort alphabetically
  const filteredDataSet = useMemo(() => {
    if (!dataSet) return []

    const base = !searchQuery
      ? dataSet
      : dataSet.filter((data) => {
        if (type === 'provider' || type === 'provider-selector') return matchesProviderSearch(data, searchQuery)
        if (type === 'studio') return matchesStudioSearch(data, searchQuery)
        const searchableText = data?.name ? data.name : data
        const textToSearch = typeof searchableText === 'string'
          ? searchableText
          : JSON.stringify(searchableText)
        return textToSearch.toLowerCase().includes(searchQuery)
      })

    if (type === 'studio') {
      return uniqBy(base, 'name')
    }

    return base
  }, [dataSet, searchQuery, type])

  const renderSearchInput = () => {
    if (type === 'provider-accordion') return null

    return (
      <div className='filter-search'>
        <div className='filter-search-input'>
          <div className='custom-form-group'>
            <input
              type='text'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={type === GAME_FILTER_SUPPORT_TYPE.STUDIO.toLowerCase() ? languageData?.searchStudios : (languageData?.searchFilters || 'Search filters...')}
              className='form-control custom-input game-filter-checkbox-search-input'
            />
            <Icon
              name={searchQuery ? 'xmark-large' : 'search'}
              width='20'
              height='20'
              className={`icon ${searchQuery ? 'clear-search' : 'search'}`}
              onClick={() => searchQuery ? setQuery('') : null}
            />
          </div>
          {searchQuery && (
            <p className='search-query-count'>
              {filteredDataSet?.length === 1
                ? languageData?.searchResultCount?.replace('{{count}}', filteredDataSet.length)
                : languageData?.searchResultsCount?.replace('{{count}}', filteredDataSet?.length ?? 0)}
            </p>
          )}
        </div>
      </div>
    )
  }

  const renderGameFiltersBody = () => {
    if (type === 'provider-accordion') {
      return (
        <GameProviderFilters
          icon={icon}
          title={title}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      )
    }

    return (
      <div className={`checklist-wrapper checklist-wrapper-${gameFilterSupportType}`}>
        {filteredDataSet?.length > 0 && filteredDataSet?.map((data, index) => {
          let name = data?.name
          if (type === 'provider' || type === 'provider-selector') {
            name = (data?.providerDisplayName || data?.name || '').trim()
          } else if (type === 'studio') {
            name = (data?.studioDisplayName || data?.name || '').trim()
          }
          return (
            <a
              className={(type === 'studio' ? selectedFilterData?.includes((data?.id ? data.id : data)) : selectedFilterData?.includes((data?.name ? data.name : data)))
                ? 'checklist-item selected'
                : 'checklist-item'}
              href={() => false}
              key={index}
              // TODO: Duplicate code fragment for onClick callback in GameFiltersDropdown.jsx
              onClick={() => {
                if (dataType === 'provider') {
                  if (selectedFilterData?.includes((data?.name ? data.name : data))) {
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, provider: selectedFilterData?.filter(ele => ele !== (data?.name ? data.name : data)) }))
                  } else {
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, provider: [...selectedFilterData, (data?.name ? data.name : data)] }))
                  }
                } else if (dataType === 'studio') {
                  if (selectedFilterData?.includes((data?.id ? data.id : data))) {
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, studio: selectedFilterData?.filter(ele => ele !== (data?.id ? data.id : data)) }))
                  } else {
                    const newStudio = [...selectedFilterData, (data?.id ? data.id : data)]
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, studio: newStudio }))
                  }
                } else if (dataType === 'rating') {
                  if (selectedFilterData?.includes((data?.name ? data.name : data))) {
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, rating: selectedFilterData?.filter(ele => ele !== (data?.name ? data.name : data)) }))
                  } else {
                    setSelectedFilter(selectedFilter => ({ ...selectedFilter, rating: [...selectedFilterData, (data?.name ? data.name : data)] }))
                  }
                }
              }}
            >
              <Icon name={(type === 'studio' ? selectedFilterData?.includes((data?.id ? data.id : data)) : selectedFilterData?.includes((data?.name ? data.name : data))) ? 'casino-filter-checked' : 'casino-filter-unchecked'} className='mr-2' width='20px' height='20px' />
              {capitalizeMyText(name || (typeof data === 'string' && data.includes('[') ? JSON.parse(data)?.join(' / ') : (data?.name || data))?.toString().trim())}
            </a>
          )
        })}
        {searchQuery && filteredDataSet?.length === 0 && (
          <p className='no-search-results'>{languageData?.noSearchResultsFound}</p>
        )}
      </div>
    )
  }

  return (
    <div className={`filter-list-block ${type}`}>
      <div className='filter-list-wrapper-header'>
        <Icon name={`${icon}-solid`} className='mr-2' width='16' height='16' />
        <span className='text'>{title}</span>
        {/* TODO: Add clear all button and its functionality */}
      </div>
      <div className='filter-list-wrapper-body'>
        {renderSearchInput()}
        {renderGameFiltersBody()}
      </div>
    </div>
  )
}

export default GameFiltersCheckbox
